import { Box, Modal } from "@mui/material";
import React, { useEffect, useState } from "react";
import useGetDevice from "../../../product/hooks/useGetDevice";
import "./TableConfirmationModal.css";

const UtensilsCrossedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m16 2-2.3 2.3a3 3 0 0 0 0 4.2l1.8 1.8a3 3 0 0 0 4.2 0L22 8" />
    <path d="M15 15 3.3 3.3a4.2 4.2 0 0 0 0 6l7.3 7.3c.7.7 2 .7 2.8 0L15 15Zm0 0 7 7" />
    <path d="m2.1 21.8 6.4-6.3" />
    <path d="m19 5-7 7" />
  </svg>
);

const TableConfirmationModal = ({
  tableConfirmationModalOpen,
  setTableConfirmationModalOpen,
  tableName,
  capacity = 2,
  onConfirm,
}) => {
  const isMobileView = useGetDevice();
  const [pax, setPax] = useState(capacity);

  const handleClose = () => {
    setTableConfirmationModalOpen(false);
  };

  useEffect(() => {
    if (tableConfirmationModalOpen) {
      setPax(capacity);
    }
  }, [tableConfirmationModalOpen, capacity]);

  const handleIncrement = () => {
    if (pax < capacity) setPax((prev) => prev + 1);
  };

  const handleDecrement = () => {
    if (pax > 1) setPax((prev) => prev - 1);
  };

  const handleInputChange = (e) => {
    let value = Number(e.target.value);
    if (isNaN(value)) return;
    if (value < 1) value = 1;
    if (value > capacity) value = capacity;
    setPax(value);
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobileView ? "90%" : "100%",
    maxWidth: "448px",
    bgcolor: "#fff",
    borderRadius: "28px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 32px 90px rgba(15, 23, 42, 0.2)",
    padding: "0",
    outline: "none",
    overflow: "hidden",
  };

  return (
    <Modal open={tableConfirmationModalOpen} onClose={handleClose}>
      <Box sx={style}>
        {/* CLOSE BUTTON */}
        <button className="tcm-close-btn" onClick={handleClose} aria-label="Close" type="button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-x"
            aria-hidden="true"
          >
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
          <span className="sr-only">Close</span>
        </button>

        {/* HEADER */}
        <div className="tcm-header">
          <div className="tcm-header-text">
            <h2 className="tcm-title" id="radix-_r_9j_" data-testid="text-occupy-dialog-title">
              Do you want to occupy this table?
            </h2>
          </div>
        </div>

        {/* CONTENT BODY */}
        <div className="tcm-body">
          {/* TABLE INFO */}
          <div className="tcm-table-info">
            <div>
              <p className="tcm-info-label-table">Table selected</p>
              <p className="tcm-table-name" data-testid="text-occupy-table-name">
                {tableName}
              </p>
            </div>
            <div className="tcm-capacity-box">
              <p className="tcm-info-label-capacity">Capacity</p>
              <p className="tcm-capacity-value" data-testid="text-occupy-table-capacity">
                {capacity} seats
              </p>
            </div>
          </div>

          {/* PAX SECTION */}
          <div className="tcm-pax-section">
            <div className="tcm-pax-header">
              <p>Guests / Pax</p>
              <span>Select between 1 and {capacity}</span>
            </div>

            <div className="tcm-pax-control">
              <button
                className="tcm-pax-btn"
                onClick={handleDecrement}
                disabled={pax === 1}
                type="button"
                data-testid="button-decrease-occupy-pax"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-minus"
                  aria-hidden="true"
                >
                  <path d="M5 12h14"></path>
                </svg>
              </button>
              <div className="tcm-pax-middle">
                <span className="tcm-pax-selected-label">Pax selected</span>
                <span className="tcm-pax-value" data-testid="text-occupy-pax-value">
                  {pax}
                </span>
              </div>
              <button
                className="tcm-pax-btn"
                onClick={handleIncrement}
                disabled={pax === capacity}
                type="button"
                data-testid="button-increase-occupy-pax"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-plus"
                  aria-hidden="true"
                >
                  <path d="M5 12h14"></path>
                  <path d="M12 5v14"></path>
                </svg>
              </button>
            </div>

            <input
              type="number"
              className="tcm-pax-input"
              value={pax}
              onChange={handleInputChange}
              min={1}
              max={capacity}
              data-testid="input-occupy-pax"
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="tcm-actions">
          <button
            type="button"
            className="tcm-btn-cancel"
            data-testid="button-cancel-occupy-table"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tcm-btn-confirm"
            data-testid="button-confirm-occupy-table"
            onClick={() => onConfirm({ pax, tableName, capacity })}
          >
            Occupy & Open Cart
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default TableConfirmationModal;
